/**
 * One analysis result onto one media draft — the mapping both ways in share.
 *
 * Out of `src/utils/media-helpers.ts` on #402, minus `getMediaName` and `getEnrichmentParams`,
 * which build the lookup request and live in `lookup.ts`.
 *
 * **This is what makes the ingest seam small.** Until this commit the same mapping existed twice:
 * here for the in-app form, and as a hand-written six-case `switch` in `EntryUpdateProvider.tsx`
 * for the external HTTP path — two implementations that disagreed on which metadata keys a captured
 * movie or book got. There is one now, and both adapters call it.
 *
 * `MediaType` left this file on #401: it was declared as `EnrichmentMediaType | 'release'` under a
 * comment saying it extended the enrichment union, and that union already carried `'release'`
 * (`lib/media-enrichment.ts:9`), so the alias added nothing. Every site reads `MediaKind`.
 */

import type { EnrichedMediaResult } from '@/enrichment'
import type { MediaEditValues } from '../media-preview/types'
import type {
  ArtistCreate,
  BookCreate,
  LabelCreate,
  MediaKind,
  MovieCreate,
  ReleaseCreate,
} from '@/entries'
import type {
  MovieAnalysis,
  BookAnalysis,
  ArtistAnalysis,
  LabelAnalysis,
  ReleaseAnalysis,
} from '../analysis/types'

/** Union of all media analysis types */
export type MediaAnalysis =
  | MovieAnalysis
  | BookAnalysis
  | ArtistAnalysis
  | LabelAnalysis
  | ReleaseAnalysis

/** Type guards for media analysis types */
export function isMovieAnalysis(
  analysis: MediaAnalysis,
): analysis is MovieAnalysis {
  return (
    'title' in analysis &&
    'status' in analysis &&
    (analysis.status === 'want_to_watch' || analysis.status === 'seen')
  )
}

export function isBookAnalysis(
  analysis: MediaAnalysis,
): analysis is BookAnalysis {
  return (
    'title' in analysis &&
    'status' in analysis &&
    (analysis.status === 'want_to_read' ||
      analysis.status === 'reading' ||
      analysis.status === 'read')
  )
}

export function isArtistAnalysis(
  analysis: MediaAnalysis,
): analysis is ArtistAnalysis {
  return (
    'name' in analysis && !('title' in analysis) && !('website' in analysis)
  )
}

export function isLabelAnalysis(
  analysis: MediaAnalysis,
): analysis is LabelAnalysis {
  return 'name' in analysis && !('title' in analysis)
}

export function isReleaseAnalysis(
  analysis: MediaAnalysis,
): analysis is ReleaseAnalysis {
  return 'title' in analysis && 'artistName' in analysis && !('bpm' in analysis)
}

/**
 * What `buildMediaCreateData` returns, per kind.
 *
 * These are `entries/media/write-spec.ts`'s create drafts, not five shapes of this file's own.
 * Until #401 the mapping produced five `*CreateData` types and `useFormSubmission.ts` held five
 * `xCreateDataToInsert` adapters to turn them into the Supabase row shapes the creates then took.
 * The creates take their own drafts now, so the adapters had nothing left to bridge and the mapping
 * builds the draft directly.
 */
export type MediaCreateData =
  | { type: 'movie'; data: MovieCreate }
  | { type: 'book'; data: BookCreate }
  | { type: 'artist'; data: ArtistCreate }
  | { type: 'label'; data: LabelCreate }
  | { type: 'release'; data: ReleaseCreate }

/**
 * Extract name/title from analysis regardless of type.
 * Handles the case where analysis type doesn't match target type.
 */
function getAnalysisName(analysis: MediaAnalysis): string {
  // Try title first (movie, book, release)
  if ('title' in analysis && analysis.title) {
    return analysis.title
  }
  // Then try name (artist, label)
  if ('name' in analysis && analysis.name) {
    return analysis.name
  }
  return 'Unknown'
}

/**
 * Build media create data by merging analysis with enrichment results.
 * Enriched data takes precedence when available.
 * Edited values from user take highest precedence.
 *
 * Note: The type parameter might differ from the analysis type when
 * music fallback logic finds a different type (e.g., AI detected "artist"
 * but enrichment found it as "label"). In these cases, enriched data
 * is the primary source of truth.
 */
export function buildMediaCreateData(
  type: MediaKind,
  analysis: MediaAnalysis,
  enrichedData: EnrichedMediaResult | null,
  editedValues?: MediaEditValues | null,
): MediaCreateData {
  const enriched = enrichedData?.enriched
  // When enriched type differs from analysis type, prioritize enriched data
  const typeChanged = enrichedData && enrichedData.type !== type

  // Get a name from analysis that works regardless of analysis structure
  const analysisName = getAnalysisName(analysis)

  switch (type) {
    case 'movie': {
      const movieAnalysis = !typeChanged ? (analysis as MovieAnalysis) : null
      return {
        type: 'movie',
        data: {
          title:
            editedValues?.title ||
            enriched?.title ||
            movieAnalysis?.title ||
            analysisName,
          director: editedValues?.director ?? enriched?.director ?? undefined,
          genres: editedValues?.genres ?? enriched?.genres ?? [],
          year:
            editedValues?.year ??
            enriched?.year ??
            movieAnalysis?.year ??
            undefined,
          status: movieAnalysis?.status ?? 'want_to_watch',
          external_id: enriched?.externalId ?? undefined,
          poster_url: enriched?.imageUrl ?? undefined,
          overview:
            editedValues?.description ?? enriched?.description ?? undefined,
        },
      }
    }
    case 'book': {
      const bookAnalysis = !typeChanged ? (analysis as BookAnalysis) : null
      return {
        type: 'book',
        data: {
          title:
            editedValues?.title ||
            enriched?.title ||
            bookAnalysis?.title ||
            analysisName,
          author:
            editedValues?.author ??
            enriched?.author ??
            bookAnalysis?.author ??
            undefined,
          topics: enriched?.topics ?? [],
          year: editedValues?.year ?? enriched?.year ?? undefined,
          status: bookAnalysis?.status ?? 'want_to_read',
          external_id: enriched?.externalId ?? undefined,
          cover_url: enriched?.imageUrl ?? undefined,
          description:
            editedValues?.description ?? enriched?.description ?? undefined,
        },
      }
    }
    case 'artist': {
      const artistAnalysis = !typeChanged ? (analysis as ArtistAnalysis) : null
      return {
        type: 'artist',
        data: {
          name:
            editedValues?.name ||
            enriched?.title ||
            artistAnalysis?.name ||
            analysisName,
          genres:
            editedValues?.genres ??
            enriched?.genres ??
            artistAnalysis?.genres ??
            [],
          external_id: enriched?.externalId ?? undefined,
          image_url: enriched?.imageUrl ?? undefined,
          bio: editedValues?.bio ?? enriched?.description ?? undefined,
        },
      }
    }
    case 'label': {
      const labelAnalysis = !typeChanged ? (analysis as LabelAnalysis) : null
      return {
        type: 'label',
        data: {
          name:
            editedValues?.name ||
            enriched?.title ||
            labelAnalysis?.name ||
            analysisName,
          genres: editedValues?.genres ?? enriched?.genres ?? undefined,
          website: editedValues?.website ?? enriched?.website ?? undefined,
          external_id: enriched?.externalId ?? undefined,
          image_url: enriched?.imageUrl ?? undefined,
          profile: editedValues?.profile ?? enriched?.description ?? undefined,
        },
      }
    }
    case 'release': {
      const releaseAnalysis = analysis as ReleaseAnalysis
      return {
        type: 'release',
        data: {
          title:
            editedValues?.title ||
            enriched?.title ||
            releaseAnalysis?.title ||
            analysisName,
          genres:
            editedValues?.genres ??
            enriched?.genres ??
            releaseAnalysis?.genres ??
            [],
          year:
            editedValues?.year ??
            enriched?.year ??
            releaseAnalysis?.year ??
            undefined,
          external_id: enriched?.externalId ?? undefined,
          cover_url: enriched?.imageUrl ?? undefined,
        },
      }
    }
  }
}
